/**
 * Heuristic filter for Klondike Solitaire deals.
 *
 * Klondike winnability is NP-hard — no fast algorithm exists to determine it
 * exactly. This module applies cheap structural checks to reject shuffles that
 * are *likely* unwinnable and favour deals with better opening prospects.
 *
 * The heuristics penalise:
 *   1. Aces buried deep in the tableau (face-down under many cards)
 *   2. Kings sitting directly on aces in the same column (trapping the ace)
 *   3. Same-suit order inversions in a single tableau column
 *      (e.g. 3♠ face-down beneath 2♠ — creates a deadlock)
 *   4. All four aces being completely inaccessible at the start
 *      (none face-up in tableau, none in the first stock cycle)
 *
 * A cumulative penalty score is compared against a threshold; deals that
 * exceed it are rejected and the caller should re-shuffle.
 */

/** Minimal card shape needed by heuristics — avoids requiring x/y coordinates. */
interface DeckCard {
    numValue: number
    type: string
    color: string
}

/**
 * Simulate the deal layout from a shuffled deck without mutating anything.
 *
 * Klondike deal: 28 cards go to tableau (7 columns: 1,2,3,4,5,6,7 cards),
 * remaining 24 go to stock. Within each column the last card is face-up.
 *
 * Returns tableau columns (bottom→top order) and the stock (top-of-stock first).
 */
function simulateDeal(deck: DeckCard[]): { tableau: DeckCard[][]; stock: DeckCard[] } {
    const tableau: DeckCard[][] = [[], [], [], [], [], [], []]
    let idx = 0

    for (let col = 0; col < 7; col++) {
        for (let row = 0; row <= col; row++) {
            tableau[col].push(deck[idx++])
        }
    }

    // Remaining cards form the stock (reversed in the real deal, but order
    // within the stock doesn't affect our column-based heuristics — we only
    // care about *which* cards are in the stock vs. tableau).
    const stock = deck.slice(idx)

    return { tableau, stock }
}

// ─── Individual penalty checks ──────────────────────────────────────────────

/** Penalty for each ace based on how deeply it's buried (face-down) in tableau. */
function aceDepthPenalty(tableau: DeckCard[][]): number {
    let penalty = 0

    for (const col of tableau) {
        const faceDownCount = col.length - 1 // all but top card are face-down
        for (let i = 0; i < faceDownCount; i++) {
            if (col[i].numValue === 1) {
                // Deeper position → higher penalty. Position 0 in a 7-card column
                // means 6 cards on top of it — worst case.
                const depth = faceDownCount - i // cards sitting on top of this ace
                penalty += depth * 3
            }
        }
    }

    return penalty
}

/**
 * Penalty when a king sits directly on top of an ace in the same column.
 * This traps the ace until the king can be moved, which requires an empty column.
 */
function kingOnAcePenalty(tableau: DeckCard[][]): number {
    let penalty = 0

    for (const col of tableau) {
        for (let i = 0; i < col.length - 1; i++) {
            if (col[i].numValue === 1 && col[i + 1].numValue === 13) {
                penalty += 8
            }
        }
    }

    return penalty
}

/**
 * Penalty for same-suit order inversions within a single tableau column.
 *
 * If a lower card of the same suit is buried beneath a higher card of that
 * suit in the same column, the lower card can never reach its foundation
 * before the higher card is moved — and the higher card needs the lower
 * card to be on the foundation first. Classic deadlock.
 *
 * Example: 3♠ at position 0, 2♠ at position 2 in the same column — the 2♠
 * needs to go to the foundation before the 3♠, but the 3♠ is blocking it.
 */
function suitInversionPenalty(tableau: DeckCard[][]): number {
    let penalty = 0

    for (const col of tableau) {
        const faceDownCount = col.length - 1
        // Only check face-down cards (indices 0 .. faceDownCount-1)
        for (let i = 0; i < faceDownCount; i++) {
            for (let j = i + 1; j < faceDownCount; j++) {
                const lower = col[i]
                const upper = col[j]
                // Same suit, and the deeper card has a HIGHER numValue than
                // the card above it — means the foundation needs the upper
                // card first, but it's buried deeper.
                if (lower.type === upper.type && lower.numValue > upper.numValue) {
                    penalty += 4
                }
            }
        }
    }

    return penalty
}

/**
 * Large penalty if no ace is immediately accessible at the start of the game.
 *
 * "Accessible" means either:
 *   - Face-up as the top card of a tableau column, OR
 *   - In the stock (reachable by cycling through it)
 *
 * If all 4 aces are face-down in the tableau, the game starts with zero
 * productive moves toward the foundations — a strong signal of a bad deal.
 */
function noAccessibleAcePenalty(tableau: DeckCard[][], stock: DeckCard[]): number {
    // Check face-up tableau tops
    for (const col of tableau) {
        if (col.length > 0 && col[col.length - 1].numValue === 1) {
            return 0 // At least one ace is face-up
        }
    }

    // Check stock
    for (const card of stock) {
        if (card.numValue === 1) {
            return 0 // At least one ace is in the stock
        }
    }

    // All aces are face-down in the tableau — very bad
    return 15
}

// ─── Main entry point ───────────────────────────────────────────────────────

/** Rejection threshold — deals scoring above this are filtered out. */
const REJECTION_THRESHOLD = 25

/**
 * Evaluate a shuffled deck and return `true` if it passes the heuristic
 * filter (i.e. the deal looks reasonably winnable).
 *
 * The deck is expected to be in shuffled order *before* dealing — the
 * function simulates the Klondike deal internally.
 */
export function isLikelyWinnable(deck: DeckCard[]): boolean {
    const { tableau, stock } = simulateDeal(deck)

    const score =
        aceDepthPenalty(tableau) +
        kingOnAcePenalty(tableau) +
        suitInversionPenalty(tableau) +
        noAccessibleAcePenalty(tableau, stock)

    return score <= REJECTION_THRESHOLD
}
